"use client";

import { ChevronRight, Sparkles } from "lucide-react";
import Image from "next/image";
import type { ProfileMagicReadiness } from "@/lib/profileMagicReadiness";
import { ProfileMagicBillinMascot } from "@/app/components/profile-magic/ProfileMagicBillinMascot";
import { AeraButton } from "@/app/components/ui/AeraButton";

function SkeletonBlock({ className }: { className: string }) {
  return (
    <div
      className={["animate-pulse rounded-md bg-aera-lightgray/50", className].join(
        " ",
      )}
      aria-hidden
    />
  );
}

export function ProfileMagicProgressCardSkeleton() {
  return (
    <div
      className="w-full xl:row-span-2 xl:col-start-2 rounded-2xl border border-aera-amberlight bg-aera-amberlight/10 p-4"
      aria-busy="true"
      aria-label="กำลังโหลด"
    >
      <div className="min-w-0 flex flex-col gap-2">
        <div className="flex items-center gap-4">
          <div className="w-full flex flex-col gap-2 md:gap-3">
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              <SkeletonBlock className="h-5 w-48" />
              <SkeletonBlock className="h-5 w-14 rounded-full" />
            </div>

            <div className="flex items-center justify-between gap-3">
              <SkeletonBlock className="h-4 w-32" />
              <SkeletonBlock className="h-4 w-8" />
            </div>

            <SkeletonBlock className="h-2.5 w-full rounded-full" />

            <SkeletonBlock className="h-3 w-full max-w-sm" />
          </div>

          <SkeletonBlock className="aspect-square w-full max-w-[100px] shrink-0 rounded-2xl md:max-w-[80px]" />
        </div>

        <SkeletonBlock className="h-10 w-full rounded-xl" />
      </div>
    </div>
  );
}

type Props = {
  readiness: ProfileMagicReadiness;
  onContinue: () => void;
  compact?: boolean;
};

export function ProfileMagicProgressCard({
  readiness,
  onContinue,
  compact = false,
}: Props) {
  const percent = Math.max(0, Math.min(100, readiness.percent));

  if (!compact && percent >= 100) return null;

  if (compact) {
    return (
      <div className="flex items-center gap-2.5">
        <div className="relative h-10 w-10 shrink-0 rounded-full bg-aera-greendark p-[2px] shadow-[0_0_10px_rgba(0,212,146,0.55)]">
          <div className="h-full w-full overflow-hidden rounded-full bg-aera-white">
            <Image
              src="/service_img/cmjcrj5ad000gg8xz8icco4jr.png"
              alt="บิลลิน"
              width={40}
              height={40}
              className="h-full w-full object-cover"
            />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <p className="whitespace-nowrap text-[12px] font-semibold leading-4 text-aera-charcoal">
            ความพร้อมในการจับคู่
          </p>
          <p className="whitespace-nowrap text-[11px] leading-4 text-aera-gray">
            Bill-Lin รู้จักร้านคุณ {percent}%
          </p>
          <div className="mt-1 h-1 overflow-hidden rounded-full bg-aera-lightgray">
            <div
              className="h-full rounded-full bg-aera-amber"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={onContinue}
          className="inline-flex shrink-0 cursor-pointer items-center self-center text-[11px] font-semibold leading-4 text-aera-amber"
        >
          ทำต่อ
          <ChevronRight className="h-3 w-3" aria-hidden />
        </button>
      </div>
    );
  }

  return (
    <div className="w-full xl:row-span-2 xl:col-start-2 rounded-2xl border border-aera-amberlight bg-aera-amberlight/10 p-4">
      <div className="min-w-0 flex flex-col gap-2">
        <div className="flex items-center gap-4">
          <div className="w-full flex flex-col gap-2 md:gap-3">
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              <span className="min-w-0 text-sm font-extrabold text-aera-charcoal flex items-center gap-1.5">
                <Sparkles className="h-5 w-5 text-aera-amber" />
                ทำให้บิลลินจับคู่แม่นขึ้น
              </span>
              <span className="shrink-0 rounded-full border border-aera-amberlight bg-aera-white px-2 pt-0.5 text-[11px] font-bold text-aera-charcoal">
                แนะนำ
              </span>
            </div>

            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-aera-charcoal/80">
                ความพร้อมในการจับคู่
              </p>
              <span className="text-sm font-black text-aera-charcoal tabular-nums">
                {percent}%
              </span>
            </div>

            <div className="h-2.5 overflow-hidden rounded-full bg-aera-lightgray/80">
              <div
                className="h-full rounded-full bg-linear-to-r from-aera-amberlight to-aera-amber"
                style={{ width: `${percent}%` }}
              />
            </div>

            <p className="text-xs font-medium text-aera-charcoal/60">
              เพิ่มข้อมูลบิลลิน เพื่อช่วยให้การจับคู่ทำงานได้ดีขึ้น
            </p>
          </div>

          <div className="flex justify-center">
            <ProfileMagicBillinMascot size="sm" variant="b7"/>
          </div>
        </div>

        <AeraButton
          type="button"
          onClick={onContinue}
          variant="secondary"
          fontWeight="medium"
          fullWidth
          iconRight={<ChevronRight className="h-4 w-4" />}
        >
          ทำต่อ 1 นาที
        </AeraButton>
      </div>
    </div>
  );
}
